import { Heading } from 'components/Heading/Heading';
import styled, { keyframes, css } from 'styled-components';
import { IStyledProps } from 'theme';

const GRAY_COLOR = '#CCCCCC';
const LIGHT_RED_COLOR = '#EEEEEE';
const CHARCOAL_COLOR = '#151515';
const PRIMARY_COLOR = 'transparent';

//The 0% frame gives the UI time to scroll to where the bubble WILL be after animated
const getMessageAppearFrames = (fromDirection: 'left' | 'right') => {
  return keyframes`
    0% {
      transform:  translate(0px,0px);
      opacity: 0;
    }
    1% {
      transform:  translate(${fromDirection === 'left' ? '' : '-'}20px,20px);
      opacity: 0;
    }
    100% {
      transform:  translate(0px,0px);
      opacity: 1;
    }
  `;
};

const fadeInKeyFrames = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const getSwingInFrames = (fromDirection: 'left' | 'right') => {
  return keyframes`
    100% {
      opacity: 0;
      ${fromDirection}: -10vw;
    }
    70% {
      opacity: 1;
      ${fromDirection}: 10px;
    }
    0% {
      opacity: 1;
      ${fromDirection}: 0;
    }
  `;
};

const getSpeakerIconFrames = (fromDirection: 'left' | 'right') => {
  const prefix = fromDirection === 'left' ? '' : '-';
  return keyframes({
    from: {
      transform: `translate(${prefix}50px, ${prefix}200px)`,
      opacity: 0,
    },
    to: {
      transform: 'translate(0, 0)',
      opacity: 1,
    },
  });
};

const getFadeInAnimation = (delay: number) => {
  return css`
    opacity: 0;
    animation: ${fadeInKeyFrames} 0.6s ease-in-out ${delay}s 1 forwards;
  `;
};

export const StyledStoryView = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
//   background: ${PRIMARY_COLOR};
  background-image: url("../images/background.webp");
  background-size: cover;
  background-position: 50% center;
  background-repeat: no-repeat;
`;

const Text  = styled.div``;

//Used for conversation feedback,homework,remember (dangerouslysetinnerhtmls)
export const StyledStoryHtmlBlock = styled(Text)`
  color: ${(props: IStyledProps) => props.theme.colors.black};
  b,
  strong {
    background-color: ${(props: IStyledProps) => props.theme.colors.black};
    color: ${PRIMARY_COLOR};
    padding: 0 2px;
    font-weight: normal;
  }

  > :first-child {
    margin-top: 0;
  }
  > :last-child {
    margin-bottom: 0;
  }
`;

// Images

interface StyledStoryImageContainerProps extends IStyledProps {
  speakerId: number;
}

const STORY_FEED_MAX_WIDTH = 400;
const STORY_FEED_PADDING = 10;
const STORY_FEED_ITEM_GAP = 20;
const IMAGE_SIZE_VH = 80;

//Image container, allows us to position a container where the image will be held
//On smaller devices, will be snapped to edge with 40% of image visible (face line)
//On larger devices, will be centered, and bumped over out of conversation space
export const StyledStoryImageContainer = styled.div`
  position: fixed;
  pointer-events: none;
  -webkit-user-select: none;
  user-select: none;

  z-index: 1;
  height: ${IMAGE_SIZE_VH}vh;
  width: ${IMAGE_SIZE_VH}vh;
  transform: translateY(calc(${(100 - IMAGE_SIZE_VH) * 0.6}vh));
  opacity: 1;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    ${(props: StyledStoryImageContainerProps) =>
      props.speakerId === 1
        ? `
        margin-top: 100px;
        transform: scale(0.3);
        left: calc(0.3 *6vh);
    `
        : `
        margin-top: 100px;
        transform: scale(0.3);
        right: calc(0.3 *6vh);
  `}
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    ${(props: StyledStoryImageContainerProps) =>
      props.speakerId === 1
        ? `
      right: calc(50% - ${IMAGE_SIZE_VH}vh - ${STORY_FEED_MAX_WIDTH / 2}px);
    `
        : `
      left: calc(50% - ${IMAGE_SIZE_VH}vh - ${STORY_FEED_MAX_WIDTH / 2}px);
    `}
  }
`;

interface StyledStoryImageProps extends IStyledProps {
  speakerId: number;
}

//Individual speaker image, left or right
export const StyledStorySpeakerImage = styled.img`
  position: absolute;
  bottom: 0;
  height: 100%;
  transition: opacity 1s;
  animation: ${(props: StyledStoryImageProps) =>
      getSwingInFrames(props.speakerId === 1 ? 'right' : 'left')}
    1s ease-in-out 2s forwards;
`;

export const ImageContainer = styled.div`
  display: flex;
`;

export const StyledStoryViewInner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;


interface ChoicesContainerProps {
  isActive: boolean;
}

export const ChoicesContainer = styled.div<ChoicesContainerProps>`
  position: relative;
  background-color: #efefef;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-content: center;

  padding: ${({ isActive }) => (isActive ? '10px 0px' : 0)};
  max-height: ${({ isActive }) => (isActive ? 'min-content' : 0)};
  overflow: ${({ isActive }) => (isActive ? '' : 'hidden')};
`;

export const FeedContainer = styled.div`
  height: 500px;
  overflow-y: scroll;
  ::-webkit-scrollbar {
    display: none;
    width: 0;
    background: transparent;
  }
  width: 100%;

  padding: 0 ${STORY_FEED_PADDING}px;
  max-width: ${STORY_FEED_MAX_WIDTH + STORY_FEED_PADDING * 2}px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 100%;
    max-height: 500px;
  }
`;

export const ConversationContainer = styled.div<ChoicesContainerProps>`
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    background: ${PRIMARY_COLOR};
    display: inline-block;
    width: 100%;
    margin: 0 auto;
    transition: all 0.5s ease;
    flex-grow: 1;
  }
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 100%;
  }
`;

export const StyledStoryViewFeedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  margin: 0 auto;
  max-width: ${STORY_FEED_MAX_WIDTH}px;
  position: relative;
  padding-bottom: 10px;
  background: ${PRIMARY_COLOR};

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    min-width: 100%;
    position: relative;
    height: 100%;
    align-items: center;
  }
`;

//The last chld in StyledStoryContent will have a margin at the bottom to allow space for actions to appear smoothly
export const StyledStoryViewFeed = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;

  > * {
    margin-top: ${STORY_FEED_ITEM_GAP}px;
  }
`;

interface StyledStorySpeakerProps extends IStyledProps {
  speakerId: number;
  showNub: boolean;
}

export const StyledStoryDialogue = styled(Text)<StyledStorySpeakerProps>`
  padding: 16px 32px 20px;
  animation: ${(props) => getMessageAppearFrames(props.speakerId === 1 ? 'left' : 'right')} 0.3s
    ease-in-out 0s;
  ${({ speakerId }) => (speakerId === 1 ? 'margin-left: 20%' : 'margin-right: 20%')};
  align-self: ${(props) => (props.speakerId === 1 ? `flex-end` : `flex-start`)};
  color: ${(props) => (props.speakerId === 1 ? props.theme.colors.primary.a : `#0b0b0b`)};
  position: relative;
  margin-top: 16px;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid;
    display: ${(props: StyledStorySpeakerProps) => (props.showNub ? 'block' : 'none')};
  }

  & + .speaker-${(props: StyledStorySpeakerProps) => props.speakerId} {
    margin-top: 8px;
  }

  ${(props: StyledStorySpeakerProps) =>
    props.speakerId === 1
      ? `
    background-color: white;
    color:black;
    border-bottom-right-radius: 0;
    &::after {
      right: 8px;
      border-right: 0;
      border-top-color: white;
    }
  `
      : `
    background-color:black;
    color: white;
    border-bottom-left-radius: 0;
    &::after {
      left: 0px;
      border-left: 0;
      border-top-color:black;
    }
  `}
`;

interface TimeLogProps {
  rightSide?: boolean;
}
export const TimeLog = styled.span<TimeLogProps>`
  font-family: ${({ theme }: IStyledProps) => theme.fonts.body};
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 2em;
  color: #737373;
  margin-top: 8px;
  ${({ rightSide }) =>
    rightSide &&
    css`
      align-items: flex-end;
      margin-left: auto;
    `}
`;

const getSwingInFramesStory = (fromDirection: 'left' | 'right') => {
  return keyframes`
    0% {
      opacity: 0;
      ${fromDirection}: -100px;
    }
    70% {
      opacity: 1;
      ${fromDirection}: 10px;
    }
    100% {
      opacity: 1;
      ${fromDirection}: 0;
    }
  `;
};

export const StyledStoryNarration = styled(Heading)`
  text-align: center;
  margin-bottom: 0;
  font-weight: normal;
  position: relative;
  animation: ${getSwingInFramesStory('right')} 0.4s ease-in-out 0s forwards;
  color: white;
`;

// Options

export const StyledStoryChoice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 ${STORY_FEED_PADDING}px;
  align-self: center;
  max-width: ${STORY_FEED_MAX_WIDTH}px;
`;

interface StyledStoryOptionProps extends IStyledProps {
  disabled: boolean;
}

// Deal with the touch devices which don't support hover
const getChoiceHoverStyles = () => {
  const hoverStyles = css`
    background-color: #001eff;
    color: ${(props: any) => props.theme.colors.white};
  `;
  return css`
    @media (hover) {
      &:hover {
        ${hoverStyles}
      }
    }

    @media (hover: none) {
      &:active {
        ${hoverStyles}
      }
    }
  `;
};

interface ChoiceOptionProps {
  disabled: boolean;
  isCorrect: boolean;
}

export const StyledStoryChoiceOption = styled.span<ChoiceOptionProps>`
  padding: 16px 32px 20px;
  margin: 8px 0;
  flex: 1;
  font-weight: 700;
  font-size: 18px;
  line-height: 1.5em;
  text-align: center;
  transition: 'opacity 3s ease-in-out';

  width: 100%;

  background-color: ${({ theme, isCorrect, disabled }) =>
    isCorrect || !disabled ? `#EFEFEF` : LIGHT_RED_COLOR};
  color: ${({ theme, isCorrect, disabled }) =>
    isCorrect || !disabled ? '#001eff' : GRAY_COLOR};
  border: 2px solid
    ${({ theme, isCorrect, disabled }) =>
      isCorrect || !disabled ? '#001eff' : GRAY_COLOR};
  ${(props: StyledStoryOptionProps) =>
    props.disabled
      ? `
      pointer-events: none;
    `
      : `
      cursor: pointer;
    `}

  ${getChoiceHoverStyles()}
`;

//Result (selected an option)

interface StyledStoryResponseProps extends IStyledProps {
  correct: boolean;
}

const INCORRECT_COLOR = `#696969`;
const CORRECT_COLOR = `#1601ff`;

export const StyledStoryResultResponse = styled(StyledStoryDialogue)`
  animation: none;
  align-self: stretch;
  color: ${(props: any) => props.theme.colors.white};
  ${(props: StyledStoryResponseProps) =>
    props.correct
      ? `
    background-color: ${CORRECT_COLOR};
    &::after {
      border-top-color: ${CORRECT_COLOR};
      right: 0;
    }
  `
      : `
    background-color: ${INCORRECT_COLOR};
    &::after {
      border-top-color: ${INCORRECT_COLOR};
      right: 0;
    }
  `}
`;

interface ResultProps {
  isCorrect: boolean;
}
export const StyledStoryResultFeedback = styled.div<ResultProps>`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${getFadeInAnimation(1)}

  background: ${({ isCorrect, theme }) => (isCorrect ? theme.colors.white : `#FBD768`)};
  border-radius: 0px;
  margin: 0;
  padding: 0 10px;
`;

export const ResultTitle = styled(Text)`
  margin-top: 10px;
  font-family: ${({ theme }: IStyledProps) => theme.fonts.body};
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 1em;
  text-align: center;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: CHARCOAL_COLOR;
  img {
    margin-bottom: -5px;
    width: 20px;
    height: 20px;
    margin-left: 3px;
  }
`;

export const ResultDescription = styled(Text)`
  font-family: ${({ theme }: IStyledProps) => theme.fonts.body};
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.8em;
  p {
    margin-left: 10px;
  }
  color: CHARCOAL_COLOR;
`;

export const ResultFeedBack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px 32px 20px;

  position: static;
  width: 475px;
  height: 118px;
  left: 0px;
  top: 0px;

  background: #ffffff;
  border-radius: 0px;
`;

// Conclusion

// export const StyledSharePanelContainer = styled.div`
//   ${StyledSharePanelInput} {
//     background: rgba(255, 255, 255, 0.5);
//   }
// `;

export const StyledStorySelectorsContainer = styled.div`
  padding-top: 10px;

  > * {
    &:not(:first-child) {
      margin-top: 10px;
    }
  }
`;

export const Speaker = styled.div<{ isRight: boolean }>`
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: ${STORY_FEED_MAX_WIDTH}px;
  margin: 4px auto;
  margin-left: auto;

  img {
    opacity: 0;
    width: 36px;
    height: 50px;
    margin: 0 10px;
    animation-delay: 2s;
  }

  ${(props) =>
    props.isRight
      ? css`
          justify-content: flex-end;
          img {
            animation: ${getSpeakerIconFrames('right')} 1s ease-in-out forwards;
          }
        `
      : css`
          justify-content: flex-start;
          img {
            animation: ${getSpeakerIconFrames('left')} 1s ease-in-out forwards;
          }
        `}
`;

export const AlecTitle = styled.h2<ChoicesContainerProps>`
  margin: 16px;
  font-family: ${({ theme }: IStyledProps) => theme.fonts.body};
  font-style: normal;
  font-weight: 400;
  letter-spacing: 1px;
  text-transform: uppercase;

  font-size: 12px;
  line-height: 1.13em;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 18px;
  }

  a {
    color: #151515;
    text-underline-offset: 2px;
  }

  text-align: center;
  color: ${({ isActive }) => (isActive ? CHARCOAL_COLOR : LIGHT_RED_COLOR)};
`;

const loading = keyframes`
  to {
    opacity: 0;
    display: none;
  }
`;

export const ConversationLoading = styled.img`
  position: absolute;
  margin-top: 50px;
  margin-left: 5px;
  align-items: flex-start;
  margin-right: auto;
  animation: ${loading} 0s ease 1.5s forwards;
`;

export const HorizontalLine = styled.div`
  background-color: #d8e2e7;
  width: 400px;
  height: 1px;
  margin: 8px auto 11px;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 350px;
  }
`;

export const ShowSkillsButton = styled.span`
  cursor: pointer;
  text-decoration: underline;
  color: #737373;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.86em;
  align-self: center;
`;

interface SkillsContainerProps {
  isOpen: boolean;
}

export const SkillsContainer = styled.div<SkillsContainerProps>`
  transition: all 0.5s;
  display: ${({ isOpen }) => (!isOpen ? 'none' : 'block')};
  opacity: 0;
`;

export const StyledContinueButtonContainer = styled.div<{ isVisible: boolean }>`
  position relative;
  width: 100%;
  max-width: ${STORY_FEED_MAX_WIDTH}px;
  padding: 10px 10px 0 10px;

  ${(props) =>
    props.isVisible
      ? css`
          opacity: 1;
          ${getFadeInAnimation(2)}
        `
      : css`
          opacity: 0;
          pointer-events: none;
        `};

  button {
    padding: 8px 0;
    border: 2px solid #001eff;
  }
`;